import { useCallback, useEffect } from 'react';
import { HeroImageBackgroundComponent } from '@/admins/SmartReports/components/Components/Images';
import type { OnComponentLoadArgs } from '@/admins/SmartReports/components/Components/types';
import type { EditSmartReportContextType } from '@/admins/SmartReports/components/EditSmartReport/EditSmartReportContext';
import { useEditSmartReport } from '@/admins/SmartReports/components/EditSmartReport/EditSmartReportContext';
import { TEXT_FIELD_PLACEHOLDER_ID } from '@/admins/SmartReports/components/Placeholders/TextFieldPlaceholder/TextFieldPlaceholder';
import { SlotEdit } from '@/admins/SmartReports/components/Slots/SlotEdit';
import { useGetPageComponent } from '@/admins/SmartReports/hooks/useGetPageComponent';
import { SmartReportComponentTypesEnum } from '@/admins/SmartReports/types';
import { WelligenceLogo } from '@/components/WelligenceLogo';
import { LogoBox, TitleBox } from '../components';
import { getCurrentMonthYear } from './helpers';
import { styles } from './styles';
import { SlotsEnum } from './types';

const useDefaultSlotData = (slotId: SlotsEnum, defaultData: string) => {
  const { selectedPage, onComponentLoad } =
    useEditSmartReport() as unknown as EditSmartReportContextType;
  const { slotData } = useGetPageComponent(selectedPage, slotId);

  // biome-ignore lint/correctness/useExhaustiveDependencies(slotData): suppress dependency slotData
  // biome-ignore lint/correctness/useExhaustiveDependencies(slotId): suppress dependency slotId
  // biome-ignore lint/correctness/useExhaustiveDependencies(onComponentLoad): suppress dependency onComponentLoad
  // biome-ignore lint/correctness/useExhaustiveDependencies(defaultData): suppress dependency defaultData
  useEffect(() => {
    // Only run on mount
    if (!slotData) {
      onComponentLoad({
        componentId: TEXT_FIELD_PLACEHOLDER_ID,
        slotId,
        data: defaultData,
      });
    }
  }, []); // We only want to run this on mount
};

export const TitlePageEdit = () => {
  // Force cast until the context is converted to TypeScript
  const { selectedPage, onComponentLoad, portfolio } =
    useEditSmartReport() as unknown as EditSmartReportContextType;

  const { slotData: backgroundData } = useGetPageComponent(selectedPage, SlotsEnum.BackgroundImage);

  const onBackgroundLoad = useCallback(
    ({ componentId, data }: OnComponentLoadArgs<string>) => {
      onComponentLoad({
        componentId,
        data,
        slotId: SlotsEnum.BackgroundImage,
      });
    },
    [onComponentLoad],
  );

  useDefaultSlotData(SlotsEnum.Title, 'Title');
  useDefaultSlotData(SlotsEnum.Subtitle, 'Upstream Report');
  useDefaultSlotData(SlotsEnum.Subtitle2, getCurrentMonthYear());

  return (
    <HeroImageBackgroundComponent.Component
      portfolio={portfolio}
      persistedData={backgroundData}
      onComponentLoad={onBackgroundLoad}
      sx={styles.titlePageRoot}
    >
      <LogoBox sx={styles.logoBox}>
        <WelligenceLogo />
      </LogoBox>

      <TitleBox sx={{ borderBottom: 'none' }}>
        <SlotEdit
          sx={styles.textFieldPlaceholder1}
          slotId={SlotsEnum.Title}
          accept={[SmartReportComponentTypesEnum.TextField]}
        />
      </TitleBox>
      <TitleBox sx={{ borderBottom: 'none' }}>
        <SlotEdit
          sx={styles.textFieldPlaceholder2}
          slotId={SlotsEnum.Subtitle}
          accept={[SmartReportComponentTypesEnum.TextField]}
        />
      </TitleBox>
      <TitleBox sx={{ borderBottom: 'none' }}>
        <SlotEdit
          sx={styles.textFieldPlaceholder3}
          slotId={SlotsEnum.Subtitle2}
          accept={[SmartReportComponentTypesEnum.TextField]}
        />
      </TitleBox>
    </HeroImageBackgroundComponent.Component>
  );
};
